import React, { useEffect, useState, useCallback } from 'react';
import { confirmAction } from '../utils/confirm';
import { useAuth } from '../context/AuthContext';
import { PROPERTY_LABELS } from './surveillance/surveillanceShared';
import PageHeader from './shared/PageHeader';
import { ui } from '../styles/shared';
import { ParamConfig, TileConfig, KioskConfig, AvailableDevice } from '../types/kiosk';
import { fetchKioskConfig, fetchKioskDevices, saveKioskConfig, fetchKioskKeys, createKioskKey, deleteKioskKey, KioskKey } from '../services/kioskApi';


// Resolve a human-readable label for a parameter, same precedence as the
// Surveillance monitor tab: static map → device_parameters.parameter_name → raw key.
const paramLabel = (param: ParamConfig): string =>
    PROPERTY_LABELS[param.key] || param.label || param.key;


// Stilar från den gemensamma stilfilen
const inp = ui.input;
const lbl = ui.label;
const btn = ui.btn;
const card = ui.card;

const BLANK_TILE = (): TileConfig => ({ device_ieee: '', label: '', parameters: [] });

const KioskAdmin: React.FC = () => {
    const { isAdmin } = useAuth();

    const [config, setConfig] = useState<KioskConfig>({ title: 'Sensoröversikt', refresh_sec: 30, tiles: [] });
    const [devices, setDevices] = useState<AvailableDevice[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // API key management
    const [newKeyLabel, setNewKeyLabel] = useState('Kiosk-surfplatta');
    const [createdKey, setCreatedKey] = useState<string | null>(null);
    const [keyCreating, setKeyCreating] = useState(false);
    const [kioskKeys, setKioskKeys] = useState<KioskKey[]>([]);
    const [keyDeleting, setKeyDeleting] = useState<number | null>(null);

    // "Add tile" form state — also used for editing (editIdx >= 0)
    const [showAddForm, setShowAddForm] = useState(false);
    const [editIdx, setEditIdx] = useState<number | null>(null);
    const [newTile, setNewTile] = useState<TileConfig>(BLANK_TILE());

    const loadKioskKeys = useCallback(async () => {
        try {
            setKioskKeys(await fetchKioskKeys());
        } catch { /* ignore */ }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [cfg, dev] = await Promise.all([fetchKioskConfig(), fetchKioskDevices()]);
            setConfig(cfg);
            setDevices(dev.devices ?? (dev as unknown as AvailableDevice[]));
        } catch (e: any) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); loadKioskKeys(); }, [load, loadKioskKeys]);

    const save = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await saveKioskConfig(config);
            setSuccess('Kiosk-konfiguration sparad.');
        } catch (e: any) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setSaving(false);
        }
    };

    const createKey = async () => {
        if (!newKeyLabel.trim()) return;
        setKeyCreating(true);
        setError(null);
        try {
            const created = await createKioskKey(newKeyLabel.trim());
            setCreatedKey(created.key);
            await loadKioskKeys();
        } catch (e: any) {
            setError(e?.response?.data?.error || e.message);
        } finally {
            setKeyCreating(false);
        }
    };

    const deleteKey = async (id: number) => {
        if (!confirmAction('Ta bort kiosk-nyckeln? Surfplattor som använder den slutar fungera.')) return;
        setKeyDeleting(id);
        try {
            await deleteKioskKey(id);
            setKioskKeys(ks => ks.filter(k => k.id !== id));
        } catch (e: any) {
            setError(e?.response?.data?.error || e.message);
        } finally {
            setKeyDeleting(null);
        }
    };

    const removeTile = (idx: number) =>
        setConfig(c => ({ ...c, tiles: c.tiles.filter((_, i) => i !== idx) }));

    const moveTile = (idx: number, dir: -1 | 1) =>
        setConfig(c => {
            const tiles = [...c.tiles];
            const swap = idx + dir;
            if (swap < 0 || swap >= tiles.length) return c;
            [tiles[idx], tiles[swap]] = [tiles[swap], tiles[idx]];
            return { ...c, tiles };
        });

    const selectedDevice = devices.find(d => d.ieee_address === newTile.device_ieee);

    const toggleNewTileParam = (param: ParamConfig) => {
        setNewTile(t => {
            const params = t.parameters || [];
            const exists = params.find(p => p.key === param.key);
            if (exists) return { ...t, parameters: params.filter(p => p.key !== param.key) };
            return { ...t, parameters: [...params, { ...param, label: paramLabel(param) }] };
        });
    };

    const updateNewTileParamLabel = (key: string, label: string) =>
        setNewTile(t => ({ ...t, parameters: (t.parameters || []).map(p => p.key === key ? { ...p, label } : p) }));

    const updateNewTileParamUnit = (key: string, unit: string) =>
        setNewTile(t => ({ ...t, parameters: (t.parameters || []).map(p => p.key === key ? { ...p, unit } : p) }));

    const updateNewTileParamThreshold = (key: string, field: 'warn_above' | 'warn_below', val: string) =>
        setNewTile(t => ({ ...t, parameters: (t.parameters || []).map(p =>
            p.key === key ? { ...p, [field]: val === '' ? '' : Number(val) } : p
        ) }));

    const addTile = () => {
        if (!newTile.device_ieee) return;
        const dev = devices.find(d => d.ieee_address === newTile.device_ieee);
        const tile: TileConfig = {
            device_ieee: newTile.device_ieee,
            label: newTile.label || dev?.name || newTile.device_ieee,
            parameters: newTile.parameters || [],
        };
        if (editIdx !== null) {
            setConfig(c => {
                const tiles = [...c.tiles];
                tiles[editIdx] = tile;
                return { ...c, tiles };
            });
            setEditIdx(null);
        } else {
            setConfig(c => ({ ...c, tiles: [...c.tiles, tile] }));
        }
        setNewTile(BLANK_TILE());
        setShowAddForm(false);
    };

    const openEdit = (idx: number) => {
        const tile = config.tiles[idx];
        setNewTile({
            device_ieee: tile.device_ieee || '',
            label: tile.label,
            parameters: (tile.parameters || []).map(p => ({ ...p })),
        });
        setEditIdx(idx);
        setShowAddForm(true);
    };

    const kioskUrl = `${window.location.origin}/kiosk`;

    if (!isAdmin) return <p style={{ padding: 24 }}>Åtkomst nekad.</p>;
    if (loading) return <p style={{ padding: 24 }}>Laddar…</p>;

    return (
        <div style={{ maxWidth: 860 }}>
            <PageHeader icon="🖥️" title="Översikt och kiosk"
                description={<>Välj vad som visas på översikten för inloggade och på den publika kioskskärmen med API-nyckel. Samma innehåll på båda. Kiosk-adress: <code style={{ background: '#f4f4f4', padding: '2px 6px', borderRadius: 3 }}>{kioskUrl}#apiKey=DIN_NYCKEL</code></>} />

            {error   && <div style={{ background: '#ffeaea', color: '#c00', padding: '10px 14px', borderRadius: 6, marginBottom: 12 }}>{error}</div>}
            {success && <div style={{ background: '#eafff0', color: '#197340', padding: '10px 14px', borderRadius: 6, marginBottom: 12 }}>{success}</div>}

            {/* API key section */}
            <div style={card}>
                <h3 style={{ marginTop: 0, fontSize: 15 }}>🔑 Kiosk API-nyckel</h3>
                <p style={{ color: '#555', fontSize: 13, marginTop: 0 }}>
                    Skapa en dedikerad kiosk-nyckel. Den fungerar <em>bara</em> för kiosk-dashboarden — inte för M2M/gateway-åtkomst. Nyckeln visas bara en gång.
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                        <label style={lbl}>Etikett</label>
                        <input style={inp} value={newKeyLabel}
                            onChange={e => setNewKeyLabel(e.target.value)} />
                    </div>
                    <button style={btn('#6c757d')} onClick={createKey} disabled={keyCreating || !newKeyLabel.trim()}>
                        {keyCreating ? 'Skapar…' : 'Skapa nyckel'}
                    </button>
                </div>
                {createdKey && (
                    <div style={{ marginTop: 14, background: '#f0f7ff', border: '1px solid #b6d4fe', borderRadius: 6, padding: '12px 14px' }}>
                        <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 6, color: '#1a56db' }}>
                            ✅ Nyckel skapad — kopiera den nu, den visas inte igen!
                        </div>
                        <code style={{
                            display: 'block', wordBreak: 'break-all', fontSize: 13,
                            background: 'white', border: '1px solid #ddd', borderRadius: 4,
                            padding: '8px 10px', userSelect: 'all',
                        }}>{createdKey}</code>
                        <div style={{ fontSize: 12, color: '#555', marginTop: 8 }}>
                            Kiosk-URL: <code style={{ background: '#eee', padding: '1px 5px', borderRadius: 3 }}>
                                {kioskUrl}#apiKey={createdKey}
                            </code>
                        </div>
                        <button style={{ ...btn('#6c757d'), marginTop: 10, fontSize: 12 }}
                            onClick={() => setCreatedKey(null)}>
                            Stäng
                        </button>
                    </div>
                )}

                {/* Existing kiosk keys list */}
                {kioskKeys.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                        <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 8, color: '#444' }}>Befintliga nycklar</div>
                        {kioskKeys.map(k => (
                            <div key={k.id} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 12px', background: '#f9f9f9', border: '1px solid #e0e0e0',
                                borderRadius: 6, marginBottom: 6,
                            }}>
                                <div>
                                    <span style={{ fontWeight: 'bold', fontSize: 13 }}>{k.label}</span>
                                    <span style={{ fontSize: 11, color: '#888', marginLeft: 10 }}>
                                        Skapad: {new Date(k.created_at).toLocaleString('sv-SE')}
                                    </span>
                                    {k.last_used_at && (
                                        <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>
                                            · Senast använd: {new Date(k.last_used_at).toLocaleString('sv-SE')}
                                        </span>
                                    )}
                                </div>
                                <button
                                    style={{ ...btn('#dc3545'), fontSize: 12, padding: '5px 10px' }}
                                    disabled={keyDeleting === k.id}
                                    onClick={() => deleteKey(k.id)}
                                >
                                    {keyDeleting === k.id ? '…' : 'Ta bort'}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Global settings */}
            <div style={card}>
                <h3 style={{ marginTop: 0, fontSize: 15 }}>Allmänt</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: 16 }}>
                    <div>
                        <label style={lbl}>Titel</label>
                        <input style={inp} value={config.title}
                            onChange={e => setConfig(c => ({ ...c, title: e.target.value }))} />
                    </div>
                    <div>
                        <label style={lbl}>Uppdateringsintervall (sek)</label>
                        <input style={inp} type="number" min={10} max={300} value={config.refresh_sec}
                            onChange={e => setConfig(c => ({ ...c, refresh_sec: Number(e.target.value) }))} />
                    </div>
                </div>
            </div>

            {/* Configured tiles */}
            <h3 style={{ fontSize: 15, marginBottom: 8 }}>Widgets på dashboarden ({config.tiles.length})</h3>
            {config.tiles.length === 0 && (
                <p style={{ color: '#888', fontSize: 13 }}>Inga widgets tillagda ännu.</p>
            )}
            {config.tiles.map((tile, idx) => {
                const displayLabel = tile.label || tile.device_ieee;
                const displaySubtext = tile.device_ieee;

                return (
                    <div key={idx} style={{ ...card, borderLeft: '4px solid #007bff' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 18 }}>🔧</span>
                                    <div style={{ fontWeight: 'bold', fontSize: 15 }}>{displayLabel}</div>
                                </div>
                                <div style={{ color: '#888', fontSize: 12, marginTop: 2, marginLeft: 26 }}>{displaySubtext}</div>
                                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, marginLeft: 26 }}>
                                    {(tile.parameters || []).map(p => (
                                        <span key={p.key} style={{
                                            background: '#e8f0fe', color: '#1a56db', borderRadius: 12,
                                            padding: '3px 10px', fontSize: 12,
                                        }}>
                                            {p.label}{p.unit ? ` (${p.unit})` : ''}
                                        </span>
                                    ))}
                                    {(!tile.parameters || tile.parameters.length === 0) && <span style={{ color: '#aaa', fontSize: 12 }}>Inga parametrar valda</span>}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                <button onClick={() => openEdit(idx)}
                                style={btn('#007bff')} title="Redigera">✏️</button>
                            <button onClick={() => moveTile(idx, -1)} disabled={idx === 0}
                                style={btn('#6c757d')} title="Flytta upp">▲</button>
                            <button onClick={() => moveTile(idx, 1)} disabled={idx === config.tiles.length - 1}
                                style={btn('#6c757d')} title="Flytta ned">▼</button>
                            <button onClick={() => removeTile(idx)} style={btn('#dc3545')} title="Ta bort">✕</button>
                        </div>
                    </div>
                </div>
                );
            })}

            {/* Add tile form */}
            {!showAddForm ? (
                <button style={{ ...btn('#28a745'), marginBottom: 20 }} onClick={() => setShowAddForm(true)}>
                    + Lägg till widget
                </button>
            ) : (
                <div style={{ ...card, borderLeft: '4px solid #28a745' }}>
                    <h4 style={{ marginTop: 0 }}>{editIdx !== null ? 'Redigera widget' : 'Lägg till widget'}</h4>
                    
                    {/* Device tile form */}
                    <div style={{ marginBottom: 14 }}>
                        <label style={lbl}>Välj enhet</label>
                        <select style={inp} value={newTile.device_ieee || ''}
                            onChange={e => setNewTile({ ...BLANK_TILE(), device_ieee: e.target.value })}>
                            <option value="">— välj —</option>
                            {devices.map(d => (
                                <option key={d.ieee_address} value={d.ieee_address}>
                                    {d.name} ({d.type})
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedDevice && (
                        <>
                            <div style={{ marginBottom: 14 }}>
                                <label style={lbl}>Visningsnamn (lämna tomt = enhetsnamn)</label>
                                <input style={inp} value={newTile.label} placeholder={selectedDevice.name}
                                    onChange={e => setNewTile(t => ({ ...t, label: e.target.value }))} />
                            </div>

                            <div style={{ marginBottom: 14 }}>
                                <label style={lbl}>Välj parametrar att visa</label>
                                {selectedDevice.parameters.length === 0 && (
                                    <p style={{ color: '#999', fontSize: 13 }}>Inga registrerade parametrar för denna enhet ännu.</p>
                                )}
                                {[...selectedDevice.parameters]
                                    .sort((a, b) => paramLabel(a).localeCompare(paramLabel(b), 'sv'))
                                    .map(param => {
                                    const selected = (newTile.parameters || []).find(p => p.key === param.key);
                                    return (
                                        <div key={param.key} style={{
                                            border: '1px solid #ddd', borderRadius: 6, padding: '10px 12px',
                                            marginBottom: 8, background: selected ? '#f0f7ff' : '#fafafa',
                                        }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                                <input type="checkbox" checked={!!selected}
                                                    onChange={() => toggleNewTileParam(param)} />
                                                <span style={{ fontWeight: 'bold' }}>{paramLabel(param)}</span>
                                                {param.unit && <span style={{ color: '#888', fontSize: 12 }}>({param.unit})</span>}
                                                {paramLabel(param) !== param.key && (
                                                    <span style={{ color: '#aaa', fontSize: 11, fontFamily: 'monospace' }}>{param.key}</span>
                                                )}
                                            </label>
                                            {selected && (
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 120px 120px', gap: 8, marginTop: 8 }}>
                                                    <div>
                                                        <label style={{ ...lbl, fontWeight: 'normal' }}>Etikett</label>
                                                        <input style={inp} value={selected.label}
                                                            onChange={e => updateNewTileParamLabel(param.key, e.target.value)} />
                                                    </div>
                                                    <div>
                                                        <label style={{ ...lbl, fontWeight: 'normal' }}>Enhet</label>
                                                        <input style={inp} value={selected.unit ?? ''}
                                                            onChange={e => updateNewTileParamUnit(param.key, e.target.value)} />
                                                    </div>
                                                    <div>
                                                        <label style={{ ...lbl, fontWeight: 'normal', color: '#c00' }}>⚠ Varna om &gt;</label>
                                                        <input style={inp} type="number" placeholder="—"
                                                            value={selected.warn_above ?? ''}
                                                            onChange={e => updateNewTileParamThreshold(param.key, 'warn_above', e.target.value)} />
                                                    </div>
                                                    <div>
                                                        <label style={{ ...lbl, fontWeight: 'normal', color: '#c00' }}>⚠ Varna om &lt;</label>
                                                        <input style={inp} type="number" placeholder="—"
                                                            value={selected.warn_below ?? ''}
                                                            onChange={e => updateNewTileParamThreshold(param.key, 'warn_below', e.target.value)} />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    <div style={{ display: 'flex', gap: 8 }}>
                        <button style={btn('#007bff')} onClick={addTile}
                            disabled={!newTile.device_ieee}>
                            {editIdx !== null ? 'Spara ändringar' : 'Lägg till'}
                        </button>
                        <button style={btn('#6c757d')} onClick={() => { setShowAddForm(false); setNewTile(BLANK_TILE()); setEditIdx(null); }}>
                            Avbryt
                        </button>
                    </div>
                </div>
            )}

            <div style={{ marginTop: 8 }}>
                <button style={btn('#007bff')} onClick={save} disabled={saving}>
                    {saving ? 'Sparar…' : '💾 Spara konfiguration'}
                </button>
            </div>
        </div>
    );
};

export default KioskAdmin;
